// src/components/ServiceNowConfigurationModal.js
import React, { useState } from "react";
import { XCircle, AlertCircle, Key, HelpCircle, ChevronRight } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import UnavailableNote from "./common/UnavailableNote";

const ServiceNowApiKeyInstructionsModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">How to Generate a ServiceNow API Key</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={22} />
          </button>
        </div>

        <div className="space-y-5 text-sm text-gray-700">
          <p className="text-gray-500">
            ServiceNow's Inbound REST API Keys (available in the Washington D.C. release and later) provide a simple, static API key for authentication.
          </p>

          {/* Step 1 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              Activate the Plugin
            </h3>
            <ol className="ml-8 list-decimal space-y-1 text-gray-600">
              <li>Go to <span className="font-medium">All &gt; Admin Center &gt; Application Manager</span></li>
              <li>Verify the plugin <span className="font-medium">"API Key and HMAC Authentication"</span> (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">com.glide.tokenbased_auth</code>) is activated</li>
            </ol>
          </div>

          {/* Step 2 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              Create an Authentication Profile
            </h3>
            <ol className="ml-8 list-decimal space-y-1 text-gray-600">
              <li>Go to <span className="font-medium">All &gt; System Web Services &gt; API Access Policies &gt; Inbound Authentication Profile</span></li>
              <li>Click <span className="font-medium">New</span></li>
              <li>Select <span className="font-medium">"Create API Key authentication profiles"</span></li>
              <li>Give it a name (e.g., "Axaris AI API Access")</li>
              <li>For <span className="font-medium">Auth Parameter</span>, select the header option for <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">x-sn-apikey</code></li>
              <li>Click <span className="font-medium">Submit</span></li>
            </ol>
          </div>

          {/* Step 3 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              Generate an API Key
            </h3>
            <ol className="ml-8 list-decimal space-y-1 text-gray-600">
              <li>Go to <span className="font-medium">All &gt; System Web Services &gt; API Access Policies &gt; REST API Key</span></li>
              <li>Click <span className="font-medium">New</span></li>
              <li>Give it a name</li>
              <li>Select a <span className="font-medium">user</span> — this determines what data the API can access. Use a service account with the <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">itil</code> role</li>
              <li>Click <span className="font-medium">Submit</span></li>
              <li><span className="font-medium text-green-700">Copy the generated token</span> — this is what you'll paste below</li>
            </ol>
          </div>

          {/* Step 4 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
              Create an Access Policy
            </h3>
            <ol className="ml-8 list-decimal space-y-1 text-gray-600">
              <li>Go to <span className="font-medium">All &gt; System Web Services &gt; API Access Policies &gt; REST API Access Policies</span></li>
              <li>Click <span className="font-medium">New</span></li>
              <li>Select <span className="font-medium">Table API</span> as the REST API</li>
              <li>Add the Authentication Profile from Step 2</li>
              <li>Click <span className="font-medium">Submit</span></li>
            </ol>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceNowConfigurationModal = ({ onClose, onSuccess }) => {
  const [instanceUrl, setInstanceUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const apiCall = useApi();
  // Ticketing needs internet access; on Restricted / AirGapped installations connecting is disabled.
  const { isModuleAvailable } = useModuleAvailability();
  const unavailable = !isModuleAvailable("ticketing");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (unavailable) return;

    if (!instanceUrl.trim()) {
      setError("Please enter your ServiceNow instance URL");
      return;
    }

    if (!apiKey.trim()) {
      setError("Please enter your ServiceNow API key");
      return;
    }

    // Normalize the URL
    let normalizedUrl = instanceUrl.trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "");

    // Validate it looks like a ServiceNow URL
    if (!normalizedUrl.endsWith(".service-now.com")) {
      setError("Please enter a valid ServiceNow URL (e.g., your-company.service-now.com)");
      return;
    }

    setLoading(true);

    try {
      await apiCall("POST", "/integrations/servicenow/connect-api-key", {
        instanceUrl: normalizedUrl,
        apiKey: apiKey.trim()
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to configure ServiceNow integration");
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-content-primary">Configure ServiceNow Integration</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              disabled={loading}
            >
              <XCircle size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Info Box */}
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                Connect your ServiceNow instance to create incidents directly from Axaris remediations.
              </p>
            </div>
            {unavailable && <UnavailableNote className="mb-4" />}

            {/* Error */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Instance URL Input */}
            <div className="mb-4">
              <label
                htmlFor="instanceUrl"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                ServiceNow Instance URL
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  https://
                </span>
                <input
                  type="text"
                  id="instanceUrl"
                  value={instanceUrl}
                  onChange={(e) => setInstanceUrl(e.target.value)}
                  placeholder="your-company.service-now.com"
                  className="flex-1 block w-full rounded-none rounded-r-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                  disabled={loading}
                />
              </div>
            </div>

            {/* API Key Input */}
            <div className="mb-4">
              <label
                htmlFor="apiKey"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                API Key
              </label>
              <input
                type="password"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste your ServiceNow API key"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                The API key generated from ServiceNow's REST API Key configuration.
              </p>
            </div>

            {/* Instructions link */}
            <button
              type="button"
              onClick={() => setShowInstructions(true)}
              className="mb-5 flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium"
            >
              <HelpCircle className="h-4 w-4" />
              How do I generate an API key?
              <ChevronRight className="h-3.5 w-3.5" />
            </button>

            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                disabled={loading || unavailable}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Connecting...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Connect
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showInstructions && (
        <ServiceNowApiKeyInstructionsModal onClose={() => setShowInstructions(false)} />
      )}
    </>
  );
};

export default ServiceNowConfigurationModal;
